import { useMemo } from 'react';
import apiClient from 'src/api/axiosClient';
import { Group } from 'src/modules/groups/types';
import {
  Contact,
  GetContactsApiParams,
  ContactWithGroups,
  GetContactsParams,
  GetContactImportsParams,
  ContactImportSummary,
  CreateContactRequestData,
  ContactsCsvResponse,
} from '../types';
import { ContactInfoRequestFormData } from '../types/ContactInfoRequestFormData';

export function useContactsApi() {
  return useMemo(
    () => ({
      getContacts: (params: GetContactsApiParams) => {
        return apiClient.get<{ items: Contact[]; count: number }>(`/contacts`, { params });
      },
      getContactsWithGroups: (params: GetContactsParams) => {
        return apiClient.get<{ items: ContactWithGroups[]; count: number }>(
          `/contacts/with-groups`,
          { params },
        );
      },
      createContacts: (companyId: string, data: CreateContactRequestData[]) => {
        return apiClient.post<Contact[]>(`/contacts`, data, { params: { companyId } });
      },
      deleteContact: (companyId: string, contactId: string) => {
        return apiClient.delete<void>(`/contacts/${contactId}`, { params: { companyId } });
      },
      assignUser: (companyId: string, contactId: string, userId: string) => {
        return apiClient.put<void>(
          `/contacts/${contactId}/assign-to`,
          { userId },
          { params: { companyId } },
        );
      },
      updateContactInfo: (
        companyId: string,
        contactId: string,
        data: ContactInfoRequestFormData | CreateContactRequestData,
      ) => {
        return apiClient.put<Contact>(`/contacts/${contactId}`, data, { params: { companyId } });
      },
      getContactGroups: (
        companyId: string,
        contactId: string,
        params?: { limit?: number; offset?: number; query?: string },
      ) => {
        return apiClient.get<{ items: Group[]; count: number }>(`/contacts/${contactId}/groups`, {
          params: { companyId, ...params },
        });
      },
      addContactToGroups: (companyId: string, contactId: string, groupIds: string[]) => {
        return apiClient.post<void>(`/contacts/${contactId}/groups`, groupIds, {
          params: { companyId },
        });
      },
      removeContactFromGroups: (companyId: string, contactId: string, groupIds: string[]) => {
        return apiClient.delete<void>(`/contacts/${contactId}/groups`, {
          params: { companyId },
          data: { groupIds },
        });
      },
      addContactCustomFields: (
        contactId: string,
        metadata: { metadataId: string; value: string }[],
      ) => {
        return apiClient.put<void>(`contact-metadata/contact/${contactId}`, metadata);
      },
      removeContactCustomFields: (contactId: string, metadataIds: string[]) => {
        return apiClient.delete<void>(`contact-metadata/contact/${contactId}`, {
          data: { metadataIds },
        });
      },
      markContactAsSpam: (companyId: string, contactId: string, value: boolean) => {
        return apiClient.put<void>(
          `/contacts/${contactId}/is-spam`,
          { value },
          { params: { companyId } },
        );
      },
      optOutContact: (contactId: string, companyId: string) => {
        return apiClient.put<void>(`/contacts/${contactId}/opt-out`, null, { params: { companyId } });
      },
      validateContacts: (companyId: string) => {
        return apiClient.post<void>(`/contacts/validate`, null, { params: { companyId } });
      },
      uploadContactsCsv: (companyId: string, file: FormData) => {
        return apiClient.post<{ taskId: string }>(`/contacts/csv-upload`, file, {
          params: { companyId },
        });
      },
      getImportContactsStatus: (companyId: string, taskId: string) => {
        return apiClient.get<ContactsCsvResponse>(`/tasks/${taskId}`, { params: { companyId } });
      },
      getContactImports: (params: GetContactImportsParams) => {
        return apiClient.get<{ items: ContactImportSummary[]; count: number }>(
          `/tasks/contact-imports`,
          { params },
        );
      },
    }),
    [],
  );
}
